/**
 * Componente Settings: Configurazione Google API
 * Scaricabile: Guida per abilitare Google Drive API
 */

import React, { useState } from 'react';
import { Download, BookOpen, AlertCircle, CheckCircle } from 'lucide-react';

export const GoogleAPISettingsGuide: React.FC = () => {
  const [downloadFormat, setDownloadFormat] = useState<'md' | 'txt'>('md');

  const guideContent = `# 🚀 Guida Setup: Google Drive API

## Cos'è?
Questo documento spiega come abilitare **Google Drive API** nel tuo Google Cloud Project per permettere al Garage Management System di salvare le foto direttamente su Google Drive.

---

## ⏱️ Tempo Richiesto
**2 minuti**

---

## 📋 Prerequisiti
- Accesso a Google Cloud Console
- Essere l'**Administrator** del progetto Google
- (Facoltativo) Il Project ID: \`1020635775383\`

---

## 🔧 Procedura Completa

### Step 1: Accedi a Google Cloud Console
1. Apri: https://console.cloud.google.com
2. Accedi con il tuo account Google
3. Seleziona il progetto: **"Tiber Garage"**

### Step 2: Naviga alla Libreria API
1. Nel menu laterale: **"API e servizi"** → **"Libreria"**
2. Oppure vai direttamente:
   https://console.cloud.google.com/apis/library?project=1020635775383

### Step 3: Abilita Google Drive API
1. Nella barra di ricerca: digita **"Google Drive API"**
2. Clicca sul risultato **"Google Drive API"**
3. Clicca il pulsante blu **"ABILITA"**
4. Attendi 10-15 secondi

### Step 4: Verifica
Dovresti vedere:
- Pulsante **"DISABILITA"** (non più "ABILITA")
- Messaggio "API abilitata"
- Sezione "Quota e analitiche"

---

## ✅ Fatto!
L'API è ora abilitata. L'app funzionerà automaticamente.

---

## 🛠️ Troubleshooting

### Non trovo il pulsante "ABILITA"
- Assicurati di aver selezionato il progetto giusto
- Aggiorna la pagina (F5)

### Errore "Accesso non autorizzato"
- Solo gli Administrator possono abilitare le API
- Chiedi all'admin del progetto

### Errore 403 quando carichi le foto
- Verifica che Google Drive API sia abilitata
- Attendi 1-2 minuti che la modifica si propaghi
- Fai un refresh dell'app (Cmd+Shift+R su Mac)

---

## 💡 Note Importanti
1. **Una sola volta**: Abilita solo 1 volta per progetto
2. **Nessun costo aggiuntivo**: Google Drive API è gratuita
3. **Sicurezza**: L'app non avrà accesso alle password Google
4. **Reversibile**: Puoi disabilitare l'API quando vuoi

---

**Versione:** 1.0
**Data:** 26 Febbraio 2026
`;

  const downloadFile = () => {
    const element = document.createElement('a');
    
    if (downloadFormat === 'md') {
      element.setAttribute(
        'href',
        'data:text/markdown;charset=utf-8,' + encodeURIComponent(guideContent)
      );
      element.setAttribute('download', 'GUIDA_SETUP_GOOGLE_DRIVE_API.md');
    } else {
      element.setAttribute(
        'href',
        'data:text/plain;charset=utf-8,' + encodeURIComponent(guideContent)
      );
      element.setAttribute('download', 'GUIDA_SETUP_GOOGLE_DRIVE_API.txt');
    }
    
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="bg-blue-100 p-3 rounded-lg">
            <BookOpen className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              🚀 Guida Setup: Google Drive API
            </h3>
            <p className="text-sm text-gray-700">
              Scarica la guida completa per abilitare Google Drive API e permettere il caricamento automatico delle foto.
            </p>
          </div>
        </div>
      </div>

      {/* Alert */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800">
          <strong>Attenzione:</strong> Questa guida deve essere seguita <strong>UNA SOLA VOLTA</strong> durante il setup iniziale del progetto. Dopodiché l'app funzionerà automaticamente.
        </div>
      </div>

      {/* Steps Overview */}
      <div className="flex flex-nowrap gap-3">
        {[
          { num: 1, title: 'Accedi', desc: 'Google Cloud Console' },
          { num: 2, title: 'Seleziona', desc: 'Progetto "Tiber Garage"' },
          { num: 3, title: 'Abilita', desc: 'Google Drive API' },
          { num: 4, title: 'Fatto', desc: '✅ Pronto!' }
        ].map((step) => (
          <div key={step.num} className="flex-1 bg-white border border-gray-200 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-blue-600 mb-1">{step.num}</div>
            <div className="text-sm font-semibold text-gray-900">{step.title}</div>
            <div className="text-xs text-gray-600">{step.desc}</div>
          </div>
        ))}
      </div>

      {/* Download Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="text-sm font-semibold text-gray-900 mb-4">📥 Scarica la Guida</h4>
        
        <div className="flex flex-col sm:flex-row gap-3">
          <select
            value={downloadFormat}
            onChange={(e) => setDownloadFormat(e.target.value as 'md' | 'txt')}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <option value="md">📄 Formato Markdown (.md)</option>
            <option value="txt">📝 Formato Testo (.txt)</option>
          </select>
          
          <button
            onClick={downloadFile}
            className="flex-1 sm:flex-initial px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg flex items-center justify-center gap-2 transition"
          >
            <Download className="w-4 h-4" />
            Scarica
          </button>
        </div>

        <p className="text-xs text-gray-600 mt-3">
          La guida include: istruzioni passo-passo, screenshot, troubleshooting, e FAQ.
        </p>
      </div>

      {/* Quick Steps */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <div className="flex items-start gap-3 mb-4">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <h4 className="font-semibold text-green-900">Steps Rapidi (2 minuti)</h4>
        </div>
        
        <ol className="space-y-2 text-sm text-green-900 list-decimal list-inside">
          <li>Apri: <code className="bg-green-100 px-2 py-1 rounded text-xs">console.cloud.google.com</code></li>
          <li>Seleziona progetto: <strong>"Tiber Garage"</strong></li>
          <li>Naviga a: <strong>API e servizi → Libreria</strong></li>
          <li>Cerca: <strong>"Google Drive API"</strong></li>
          <li>Clicca: <strong>"ABILITA"</strong></li>
          <li>Attendi 10-15 secondi ✅</li>
        </ol>
      </div>

      {/* Why Needed */}
      <div className="bg-gray-50 rounded-lg p-6 space-y-3">
        <h4 className="font-semibold text-gray-900">❓ Perché serve?</h4>
        <ul className="space-y-2 text-sm text-gray-700">
          <li className="flex gap-2">
            <span className="text-blue-600">✓</span>
            <span>Permette all'app di salvare le foto automaticamente su Google Drive</span>
          </li>
          <li className="flex gap-2">
            <span className="text-blue-600">✓</span>
            <span>Gli utenti non devono configurare nulla dopo il setup</span>
          </li>
          <li className="flex gap-2">
            <span className="text-blue-600">✓</span>
            <span>Le foto sono automaticamente backup e condivisibili</span>
          </li>
          <li className="flex gap-2">
            <span className="text-blue-600">✓</span>
            <span>Nessun costo aggiuntivo (Google Drive API è gratuita)</span>
          </li>
        </ul>
      </div>

      {/* Support */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900">
          <strong>Hai problemi?</strong> Contatta l'amministratore del progetto o consulta la sezione "Troubleshooting" della guida scaricata.
        </p>
      </div>
    </div>
  );
};

export default GoogleAPISettingsGuide;
